import {
  ISSUE_REFRESH_TOKEN_MUTATION,
  LOGIN_MUTATION,
  REFRESH_TOKEN_MUTATION,
} from "@/graphql/auth";
import { apolloClient } from "@/lib/apollo/apollo-client";
import type { User } from "@/lib/store/auth-enhanced";

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
}

export class AuthError extends Error {
  constructor(
    message: string,
    public code:
      | "invalidCredentials"
      | "tokenExpired"
      | "tokenInvalid"
      | "userNotFound"
      | "userDisabled"
      | "sessionExpired"
      | "unauthorized"
      | "networkError"
  ) {
    super(message);
    this.name = "AuthError";
  }
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const { data } = await apolloClient.mutate({
        mutation: LOGIN_MUTATION,
        variables: { input: credentials },
      });

      if (!data?.login) {
        throw new AuthError("Invalid credentials", "invalidCredentials");
      }

      return {
        user: data.login.user,
        tokens: {
          accessToken: data.login.accessToken,
          refreshToken: data.login.refreshToken ?? undefined,
        },
      };
    } catch (error: unknown) {
      if (error instanceof AuthError) throw error;

      // Handle ApolloError with GraphQL errors in extensions
      if (error instanceof Error && "graphQLErrors" in error) {
        const apolloError = error as {
          graphQLErrors: Array<{ message: string; extensions?: { code?: unknown } }>;
        };
        if (apolloError.graphQLErrors?.length > 0) {
          const gqlError = apolloError.graphQLErrors[0];
          const errorCode =
            typeof gqlError.extensions?.code === "string" ? gqlError.extensions.code : undefined;

          switch (errorCode) {
            case "invalidCredentials":
              throw new AuthError("Invalid email or password", "invalidCredentials");
            case "userNotFound":
              throw new AuthError("Auth: User not found", "userNotFound");
            case "userDisabled":
              throw new AuthError("Auth: User account is disabled", "userDisabled");
            case "tokenExpired":
              throw new AuthError("Auth: Session has expired", "tokenExpired");
            case "tokenInvalid":
              throw new AuthError("Auth: Invalid token", "tokenInvalid");
            case "sessionExpired":
              throw new AuthError("Auth: Session has expired", "sessionExpired");
            case "unauthorized":
              throw new AuthError("Auth: Unauthorized access", "unauthorized");
            default:
              throw new AuthError(
                gqlError.message || "Authentication failed",
                "invalidCredentials"
              );
          }
        }
      }

      // Handle network errors
      if (error instanceof Error && "networkError" in error) {
        throw new AuthError(
          "Unable to connect to server. Please check your connection.",
          "networkError"
        );
      }

      throw new AuthError("An unexpected error occurred", "networkError");
    }
  }

  /**
   * Validate auto-login token from onboarding completion
   * This token is a pre-authenticated access token that allows immediate login
   */
  async validateAutoLoginToken(token: string): Promise<AuthResponse> {
    try {
      // Validate token format
      const { isValid } = this.validateToken(token);
      if (!isValid) {
        throw new AuthError("Invalid or expired token", "tokenInvalid");
      }

      // Use the token to fetch user data via ME_QUERY
      // This will be done by the calling code since we need the lazy query
      // Here we just return the token as valid
      return {
        user: {} as User, // Will be populated by ME_QUERY
        tokens: {
          accessToken: token,
        },
      };
    } catch (error) {
      if (error instanceof AuthError) throw error;
      throw new AuthError("Token validation failed", "tokenInvalid");
    }
  }

  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const { data } = await apolloClient.mutate({
      mutation: REFRESH_TOKEN_MUTATION,
      variables: { input: { refreshToken } },
      context: { skipAuthRefresh: true },
      fetchPolicy: "no-cache",
      errorPolicy: "none",
    });

    if (!data?.refreshToken?.accessToken) {
      throw new AuthError("Token refresh failed", "sessionExpired");
    }

    return {
      accessToken: data.refreshToken.accessToken,
      refreshToken: data.refreshToken.refreshToken ?? refreshToken,
    };
  }

  async issueRefreshToken(): Promise<string> {
    const { data } = await apolloClient.mutate({
      mutation: ISSUE_REFRESH_TOKEN_MUTATION,
      fetchPolicy: "no-cache",
      errorPolicy: "none",
    });

    if (!data?.issueRefreshToken) {
      throw new AuthError("Could not issue refresh token", "unauthorized");
    }

    return data.issueRefreshToken;
  }

  validateToken(token: string): { isValid: boolean; expiresAt: number | null } {
    if (!token) return { isValid: false, expiresAt: null };

    try {
      const parts = token.split(".");
      if (parts.length !== 3) return { isValid: false, expiresAt: null };

      const payload = JSON.parse(atob(parts[1]));
      // JWT validation requires native Date for timestamp comparison
      // eslint-disable-next-line no-restricted-syntax, no-restricted-globals
      const currentTime = Math.floor(Date.now() / 1000);

      if (!payload.exp) return { isValid: true, expiresAt: null };

      return {
        isValid: payload.exp > currentTime,
        expiresAt: payload.exp * 1000,
      };
    } catch {
      return { isValid: false, expiresAt: null };
    }
  }

  isTokenExpiringSoon(token: string, bufferSeconds = 300): boolean {
    const { isValid, expiresAt } = this.validateToken(token);
    if (!isValid || !expiresAt) return true;

    const bufferMs = bufferSeconds * 1000;
    // JWT validation requires native Date for timestamp comparison
    // eslint-disable-next-line no-restricted-syntax, no-restricted-globals
    return expiresAt - Date.now() < bufferMs;
  }
}

export const authService = new AuthService();
